import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserSessions } from "../../store/slices/userSessionSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const UserSessionList = ({ caseId, onAddSession, onEditSession, onDeleteSession }) => {
    const dispatch = useDispatch();
    const { userSessions, isLoading, error } = useSelector((state) => state.userSessions);

    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (caseId) {
            dispatch(fetchUserSessions({ case_id: caseId }));
        } else {
            dispatch(fetchUserSessions());
        }
    }, [dispatch, caseId]);

    const filteredSessions = (userSessions || []).filter((s) => {
        const patientName = s.patient ? `${s.patient.first_name || ""} ${s.patient.last_name || ""}` : "";
        const categoryName = s.category?.name || "";
        const sessionTypeName = s.session_master?.name || "";
        const subSessionTypeName = s.sub_session_master?.name || "";
        const rowText = `${s.session_number || ""} ${patientName} ${categoryName} ${sessionTypeName} ${subSessionTypeName} ${s.satisfaction || ""} ${s.date || ""}`;
        return rowText.toLowerCase().includes(filterText.toLowerCase());
    });

    const totalRows = filteredSessions.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        {
            name: "Session No",
            selector: (row) => row.session_number || "",
            sortable: true,
            width: "120px",
        },
        {
            name: "Patient Name",
            selector: (row) => row.patient ? `${row.patient.first_name || ""} ${row.patient.last_name || ""}`.trim() : "",
            sortable: true,
            width: "150px",
        },
        {
            name: "Category",
            selector: (row) => row.category?.name || "N/A",
            sortable: true,
            width: "150px",
        },
        {
            name: "Session Type",
            selector: (row) => row.session_master?.name || "",
            sortable: true,
            width: "150px",
        },
        {
            name: "Sub Session",
            selector: (row) => row.sub_session_master?.name || "",
            sortable: true,
            width: "150px",
        },
        {
            name: "Date",
            selector: (row) => row.date ? row.date.split("T")[0] : "",
            sortable: true,
            width: "110px",
        },
        {
            name: "Time",
            selector: (row) => row.time || "",
            sortable: true,
            width: "90px",
        },
        {
            name: "Satisfaction",
            selector: (row) => row.satisfaction || "",
            sortable: true,
            cell: (row) => {
                let badgeClass = "secondary";
                if (row.satisfaction === "Very good" || row.satisfaction === "Good") badgeClass = "success";
                else if (row.satisfaction === "Average") badgeClass = "warning";
                else if (row.satisfaction === "Bad" || row.satisfaction === "Very bad") badgeClass = "danger";
                
                return (
                    <span className={`badge bg-label-${badgeClass}`}>
                        {row.satisfaction || "N/A"}
                    </span>
                );
            },
            width: "120px",
        },
        {
            name: "Created By",
            selector: (row) => row.creator?.name || "",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditSession && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Session"
                            onClick={() => onEditSession(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    <Link
                        to={`/sessions/print/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        title="Print Session"
                    >
                        <i className="ri-printer-line"></i>
                    </Link>
                    {onDeleteSession && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Session"
                            onClick={() => onDeleteSession(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "160px",
        },
    ];

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text(caseId ? "Case Sessions List" : "User Sessions List", 14, 10);
        const tableColumn = [
            "S.No",
            "Session No",
            "Patient Name",
            "Category",
            "Session Type",
            "Sub Session Type",
            "Date",
            "Time",
            "Satisfaction",
        ];
        const tableRows = [];

        filteredSessions.forEach((s, index) => {
            const patientName = s.patient ? `${s.patient.first_name || ""} ${s.patient.last_name || ""}` : "";
            tableRows.push([
                index + 1,
                s.session_number || "",
                patientName,
                s.category?.name || "N/A",
                s.session_master?.name || "",
                s.sub_session_master?.name || "",
                s.date ? s.date.split("T")[0] : "",
                s.time || "",
                s.satisfaction || "",
            ]);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        doc.save(caseId ? `case_${caseId}_sessions_list.pdf` : "sessions_list.pdf");
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredSessions.map((s, index) => ({
                                        "S.No": index + 1,
                                        "Session Number": s.session_number || "",
                                        "Patient Name": s.patient ? `${s.patient.first_name || ""} ${s.patient.last_name || ""}` : "",
                                        Category: s.category?.name || "N/A",
                                        "Session Type": s.session_master?.name || "",
                                        "Sub Session Type": s.sub_session_master?.name || "",
                                        Date: s.date ? s.date.split("T")[0] : "",
                                        Time: s.time || "",
                                        Satisfaction: s.satisfaction || "",
                                        "Summary of Session": s.summary_of_session || "",
                                        "Satisfaction Feedback": s.satisfaction_feedback || "",
                                    }))}
                                    filename={caseId ? `case_${caseId}_sessions_export.csv` : "sessions_export.csv"}
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search sessions..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} sessions
                </h6>

                {/* Right: Add Button */}
                <div>
                    {onAddSession && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add Session"
                            onClick={onAddSession}
                        >
                            <i className="ri-add-line me-1"></i>
                            Add Session
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredSessions}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={isLoading}
                    noDataComponent={
                        <div className="p-3 text-muted">
                            {error ? error : "No sessions found for this case"}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default UserSessionList;
